/**
 * Hook: useMoveCardToPipelineStage
 *
 * Move um card manualmente para qualquer etapa, dentro da mesma pipeline
 * ou de outra pipeline (transferência). Generaliza o padrão já usado em
 * useCardDropActions.moveToComercialPipeline para um destino arbitrário.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth';
import { PipelineCard } from '../types';
import { pipelineBoardKey } from './queryKeys';

interface MoveCardToPipelineStageInput {
  card: PipelineCard;
  destPipelineId: string;
  destStageId: string;
}

interface MoveCardToPipelineStageResult {
  crossPipeline: boolean;
  destPipelineId: string;
}

export function useMoveCardToPipelineStage() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const moveCard = useMutation({
    mutationFn: async ({
      card,
      destPipelineId,
      destStageId,
    }: MoveCardToPipelineStageInput): Promise<MoveCardToPipelineStageResult> => {
      if (!profile?.uuid) throw new Error('Usuário não autenticado');

      const crossPipeline = destPipelineId !== card.pipeline_id;

      const updatePayload = crossPipeline
        ? { pipeline_id: destPipelineId, stage_id: destStageId, data_ultima_atualizacao: new Date().toISOString() }
        : { stage_id: destStageId, data_ultima_atualizacao: new Date().toISOString() };

      const { error } = await supabase
        .from('pipeline_cards')
        .update(updatePayload)
        .eq('id', card.id);

      if (error) throw new Error(error.message);

      await supabase.from('card_history').insert({
        card_id: card.id,
        usuario_id: profile.uuid,
        acao: crossPipeline ? 'transferido' : 'movido_para_stage',
        stage_anterior_id: card.stage_id,
        stage_novo_id: destStageId,
        motivo: crossPipeline ? 'Movido manualmente' : undefined,
      });

      return { crossPipeline, destPipelineId };
    },
    onSuccess: ({ crossPipeline, destPipelineId }, { card }) => {
      queryClient.invalidateQueries({ queryKey: pipelineBoardKey(card.pipeline_id) });
      if (crossPipeline) {
        queryClient.invalidateQueries({ queryKey: pipelineBoardKey(destPipelineId) });
      }
      queryClient.invalidateQueries({ queryKey: ['pipeline-transferred', card.pipeline_id] });
      queryClient.invalidateQueries({ queryKey: ['client-journey'] });
    },
  });

  return { moveCard };
}
